import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getStudentFullDetail, getAdminMentors, reassignStudent, getStudentPortions, removeStudent, raiseClassOver } from '../lib/api';
import type { StudentFullDetail, MentorInfo, PortionsData } from '../lib/api';
import { useStudentCard } from '../context/StudentCardContext';
import { getStudentQuickStats } from '../lib/api';
import type { StudentQuickStats } from '../lib/api';
import { CurriculumRoadmap } from './CurriculumRoadmap';

// ─── Shared CSS ───────────────────────────────────────────────────────────────

const STYLES = `
@keyframes scPopIn {
  0%   { opacity:0; transform:translate(-50%,-50%) scale(.82); }
  65%  { opacity:1; transform:translate(-50%,-50%) scale(1.02); }
  100% { opacity:1; transform:translate(-50%,-50%) scale(1); }
}
@keyframes scFadeBg { from{opacity:0} to{opacity:1} }
@keyframes scHoverIn {
  0%   { opacity:0; transform:scale(.82) translateY(6px); }
  60%  { opacity:1; transform:scale(1.03) translateY(-1px); }
  100% { opacity:1; transform:scale(1)    translateY(0); }
}
@keyframes scRing {
  from { stroke-dashoffset: var(--dash-total); }
  to   { stroke-dashoffset: var(--dash-empty); }
}
@keyframes scBar  { from{width:0} to{width:var(--bar-w)} }
@keyframes scFadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
@keyframes scSpin   { to{transform:rotate(360deg)} }
.sc-modal-bg  { animation: scFadeBg .2s ease both; }
.sc-modal-box { animation: scPopIn .3s cubic-bezier(.34,1.36,.64,1) both; }
.sc-hover     { animation: scHoverIn .28s cubic-bezier(.34,1.56,.64,1) both; }
.sc-hover .ring-track { animation: scRing .7s .15s cubic-bezier(.4,0,.2,1) both; }
.sc-hover .bar-fill   { animation: scBar  .6s .2s  cubic-bezier(.4,0,.2,1) both; }
.sc-hover .su-row     { animation: scFadeUp .3s both; }
.sc-hover .su-row:nth-child(1){animation-delay:.06s}
.sc-hover .su-row:nth-child(2){animation-delay:.14s}
.sc-hover .su-row:nth-child(3){animation-delay:.22s}
.sc-hover .su-row:nth-child(4){animation-delay:.30s}
.sc-tab { border:none;background:none;cursor:pointer;transition:all .15s; }
.sc-tab:hover { color:#fff!important; }
.sc-btn-hover:hover { opacity:.85; }
`;

// ─── Tiny helpers ─────────────────────────────────────────────────────────────

function Overlay({ onClick, zIndex = 10000 }: { onClick?: () => void; zIndex?: number }) {
  return (
    <div className="sc-modal-bg" onClick={onClick} style={{
      position:'fixed', inset:0, zIndex,
      background:'rgba(0,0,0,.65)', backdropFilter:'blur(5px)',
    }} />
  );
}

function ProgBar({ pct, color }: { pct: number; color: string }) {
  return (
    <div style={{ height:6, borderRadius:3, background:'rgba(255,255,255,.08)', overflow:'hidden', flex:1 }}>
      <div className="bar-fill" style={{
        height:'100%', borderRadius:3, background:color,
        '--bar-w':`${pct}%`, width:`${pct}%`,
      } as React.CSSProperties} />
    </div>
  );
}

function Pill({ label, value, color }: { label:string; value:string|number; color?:string }) {
  return (
    <div style={{
      display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',
      padding:'12px 10px',borderRadius:12,background:'rgba(255,255,255,.05)',
      border:'1px solid rgba(255,255,255,.08)',minWidth:78,
    }}>
      <div style={{ color:color||'#fff', fontWeight:900, fontSize:20, lineHeight:1 }}>{value}</div>
      <div style={{ color:'rgba(255,255,255,.38)', fontSize:9, fontWeight:700, letterSpacing:.8, textTransform:'uppercase', marginTop:5 }}>{label}</div>
    </div>
  );
}

const ATT_COLOR = (p:number) => p>=75?'#4ade80':p>=50?'#fbbf24':'#f87171';
const STATUS_COLORS: Record<string,string> = {
  Active:'#4ade80', Inactive:'#94a3b8', 'Ran Away':'#f87171', 'Institute Leave':'#fb923c',
};
const TASK_COLOR = (s:string) =>
  s==='Completed' ? {bg:'rgba(74,222,128,.13)',c:'#4ade80'}
  : s==='Not Completed' ? {bg:'rgba(248,113,113,.13)',c:'#f87171'}
  : s==='Task Given' ? {bg:'rgba(251,191,36,.13)',c:'#fbbf24'}
  : {bg:'rgba(255,255,255,.06)',c:'rgba(255,255,255,.25)'};

// ─── HOVER CARD ───────────────────────────────────────────────────────────────

const statsCache = new Map<string, StudentQuickStats>();

export const StudentHoverCard: React.FC<{
  studentId: string; studentName: string;
  pos: { x: number; y: number };
  onCardEnter?: () => void; onCardLeave?: () => void;
  onViewDetails?: () => void; onReassign?: () => void; onRemoveSlot?: () => void;
}> = ({ studentId, studentName, pos, onCardEnter, onCardLeave, onViewDetails, onReassign, onRemoveSlot }) => {
  const [stats, setStats] = useState<StudentQuickStats | null>(statsCache.get(studentId) ?? null);
  const [loading, setLoading] = useState(!statsCache.has(studentId));

  useEffect(() => {
    if (statsCache.has(studentId)) { setStats(statsCache.get(studentId)!); setLoading(false); return; }
    setLoading(true);
    getStudentQuickStats(studentId)
      .then(r => { statsCache.set(studentId, r.stats); setStats(r.stats); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [studentId]);

  const vpW = window.innerWidth, vpH = window.innerHeight;
  const cardW = 272, cardH = onViewDetails ? 350 : 290;
  const left = pos.x + cardW + 14 > vpW ? pos.x - cardW - 8 : pos.x + 14;
  const top  = pos.y + cardH + 8  > vpH ? pos.y - cardH + 10 : pos.y - 10;

  const initials = studentName.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase();
  const R=28, CIRC=2*Math.PI*R;
  const attOff = stats ? CIRC*(1-stats.attPct/100) : CIRC;

  return (
    <div className="sc-hover" onMouseEnter={onCardEnter} onMouseLeave={onCardLeave} style={{
      position:'fixed', left, top, zIndex:9999, width:cardW, pointerEvents:'all',
      background:'linear-gradient(145deg,#1a1330 0%,#2b2240 60%,#3d2f5e 100%)',
      borderRadius:18, boxShadow:'0 20px 60px rgba(0,0,0,.55),0 0 0 1px rgba(240,180,41,.18)',
      padding:16,
    }}>
      {/* Header */}
      <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:14 }}>
        <div style={{
          width:40,height:40,borderRadius:12,flexShrink:0,display:'flex',
          alignItems:'center',justifyContent:'center',fontWeight:900,fontSize:14,color:'#fff',
          background:'linear-gradient(135deg,#7c3aed,#a855f7)',
          boxShadow:'0 4px 12px rgba(124,58,237,.5)',
        }}>{initials}</div>
        <div style={{ minWidth:0 }}>
          <div style={{ color:'#fff',fontWeight:800,fontSize:13,lineHeight:1.2,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{studentName}</div>
          <div style={{ color:'#a78bfa',fontSize:10,fontWeight:600,marginTop:2 }}>{studentId}</div>
        </div>
      </div>

      {loading ? (
        <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
          {[80,60,70].map((w,i)=>(
            <div key={i} style={{ height:10,borderRadius:6,background:'rgba(255,255,255,.08)',width:`${w}%` }}/>
          ))}
        </div>
      ) : stats ? (
        <>
          {/* Attendance ring */}
          <div className="su-row" style={{ display:'flex',alignItems:'center',gap:14,marginBottom:12 }}>
            <svg width={68} height={68} style={{ flexShrink:0 }}>
              <circle cx={34} cy={34} r={R} fill="none" stroke="rgba(255,255,255,.08)" strokeWidth={6}/>
              <circle className="ring-track" cx={34} cy={34} r={R} fill="none"
                stroke={ATT_COLOR(stats.attPct)} strokeWidth={6} strokeLinecap="round"
                strokeDasharray={CIRC}
                style={{ '--dash-total':CIRC,'--dash-empty':attOff,strokeDashoffset:attOff,transform:'rotate(-90deg)',transformOrigin:'34px 34px' } as React.CSSProperties}
              />
              <text x={34} y={34} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={12} fontWeight={800}>{stats.attPct}%</text>
            </svg>
            <div>
              <div style={{ color:'rgba(255,255,255,.45)',fontSize:9,fontWeight:700,letterSpacing:1,textTransform:'uppercase',marginBottom:4 }}>Attendance</div>
              <div style={{ color:'#fff',fontWeight:800,fontSize:18,lineHeight:1 }}>{stats.attended}<span style={{ color:'rgba(255,255,255,.4)',fontWeight:500,fontSize:12 }}>/{stats.total}</span></div>
              <div style={{ color:'rgba(255,255,255,.4)',fontSize:10,marginTop:3 }}>classes attended</div>
            </div>
          </div>

          {/* Tasks */}
          {stats.taskTotal > 0 && (
            <div className="su-row" style={{ marginBottom:12 }}>
              <div style={{ display:'flex',justifyContent:'space-between',marginBottom:5 }}>
                <span style={{ color:'rgba(255,255,255,.45)',fontSize:9,fontWeight:700,letterSpacing:1,textTransform:'uppercase' }}>Tasks</span>
                <span style={{ color:'#a78bfa',fontSize:10,fontWeight:700 }}>{stats.taskDone}/{stats.taskTotal}</span>
              </div>
              <div style={{ marginBottom:5 }}>
                <div style={{ display:'flex',justifyContent:'space-between',marginBottom:3 }}>
                  <span style={{ color:'#4ade80',fontSize:10,fontWeight:600 }}>✓ Completed</span>
                  <span style={{ color:'#4ade80',fontSize:10,fontWeight:700 }}>{stats.taskDonePct}%</span>
                </div>
                <div style={{ height:5,borderRadius:3,background:'rgba(255,255,255,.08)',overflow:'hidden' }}>
                  <div className="bar-fill" style={{ height:'100%',borderRadius:3,background:'linear-gradient(90deg,#22c55e,#4ade80)','--bar-w':`${stats.taskDonePct}%`,width:`${stats.taskDonePct}%` } as React.CSSProperties}/>
                </div>
              </div>
              <div>
                <div style={{ display:'flex',justifyContent:'space-between',marginBottom:3 }}>
                  <span style={{ color:'#f87171',fontSize:10,fontWeight:600 }}>✗ Missed</span>
                  <span style={{ color:'#f87171',fontSize:10,fontWeight:700 }}>{stats.taskTotal?Math.round(stats.taskMiss/stats.taskTotal*100):0}%</span>
                </div>
                <div style={{ height:5,borderRadius:3,background:'rgba(255,255,255,.08)',overflow:'hidden' }}>
                  <div className="bar-fill" style={{ height:'100%',borderRadius:3,background:'linear-gradient(90deg,#ef4444,#f87171)','--bar-w':`${stats.taskTotal?Math.round(stats.taskMiss/stats.taskTotal*100):0}%`,width:`${stats.taskTotal?Math.round(stats.taskMiss/stats.taskTotal*100):0}%`,animationDelay:'.28s' } as React.CSSProperties}/>
                </div>
              </div>
            </div>
          )}

          {/* Last class */}
          {stats.recent && (
            <div className="su-row" style={{ marginBottom:12,padding:'8px 10px',borderRadius:10,background:'rgba(255,255,255,.05)' }}>
              <div style={{ color:'rgba(255,255,255,.4)',fontSize:9,fontWeight:700,letterSpacing:1,textTransform:'uppercase',marginBottom:4 }}>Last Class</div>
              <div style={{ color:'#fff',fontSize:11,fontWeight:600 }}>{stats.recent.topic||'—'}</div>
              <div style={{ display:'flex',alignItems:'center',gap:6,marginTop:4 }}>
                <span style={{ color:'rgba(255,255,255,.35)',fontSize:10 }}>{stats.recent.date}</span>
                <span style={{ fontSize:9,fontWeight:700,padding:'1px 6px',borderRadius:4,background:stats.recent.attendance==='Present'?'rgba(74,222,128,.15)':'rgba(248,113,113,.15)',color:stats.recent.attendance==='Present'?'#4ade80':'#f87171' }}>{stats.recent.attendance}</span>
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ color:'rgba(255,255,255,.4)',fontSize:12,textAlign:'center',padding:'12px 0' }}>No class data yet</div>
      )}

      {/* Actions */}
      <div className="su-row" style={{ display:'flex',flexDirection:'column',gap:6,marginTop:6 }}>
        {onViewDetails && (
          <button onClick={onViewDetails} className="sc-btn-hover" style={{
            width:'100%',padding:'8px 0',borderRadius:9,border:'1.5px solid rgba(240,180,41,.4)',
            background:'rgba(240,180,41,.1)',color:'#F0B429',fontSize:11,fontWeight:800,cursor:'pointer',
          }}>⊞ View Full Details</button>
        )}
        <div style={{ display:'flex',gap:6 }}>
          {onReassign && (
            <button onClick={onReassign} className="sc-btn-hover" style={{
              flex:1,padding:'7px 0',borderRadius:9,border:'1.5px solid rgba(167,139,250,.4)',
              background:'rgba(167,139,250,.1)',color:'#c4b5fd',fontSize:11,fontWeight:700,cursor:'pointer',
            }}>↔ Reassign</button>
          )}
          {onRemoveSlot && (
            <button onClick={onRemoveSlot} className="sc-btn-hover" style={{
              flex:1,padding:'7px 0',borderRadius:9,border:'1.5px solid rgba(248,113,113,.35)',
              background:'rgba(248,113,113,.08)',color:'#fca5a5',fontSize:11,fontWeight:700,cursor:'pointer',
            }}>◑ Remove</button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── REASSIGN MENTOR MODAL ────────────────────────────────────────────────────

export const ReassignMentorModal: React.FC<{
  studentId: string; studentName: string;
  currentMentorId: string; currentMentorName: string;
  adminPassword: string;
  onClose: () => void;
  onDone: (newMentorId: string) => void;
}> = ({ studentId, studentName, currentMentorId, currentMentorName, adminPassword, onClose, onDone }) => {
  const [mentors, setMentors] = useState<MentorInfo[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingM, setLoadingM] = useState(true);

  useEffect(() => {
    getAdminMentors(adminPassword)
      .then(r => setMentors(r.mentors.filter((m:any) => m.status==='Active' && m.mentorId!==currentMentorId)))
      .catch(err => toast.error(err.message))
      .finally(() => setLoadingM(false));
  }, [adminPassword, currentMentorId]);

  const confirm = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await reassignStudent(adminPassword, studentId, selectedId);
      toast.success(`${studentName} reassigned`);
      onDone(selectedId);
    } catch(err:any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const chosen = mentors.find(m => m.mentorId === selectedId);

  return (
    <>
      <Overlay onClick={onClose} zIndex={10100} />
      <div className="sc-modal-box" style={{
        position:'fixed',top:'50%',left:'50%',zIndex:10101,
        width:440,maxWidth:'calc(100vw - 32px)',
        background:'linear-gradient(145deg,#1a1330,#2b2240)',
        borderRadius:22,padding:28,
        boxShadow:'0 32px 80px rgba(0,0,0,.7),0 0 0 1px rgba(240,180,41,.2)',
      }}>
        <div style={{ display:'flex',alignItems:'flex-start',gap:14,marginBottom:24 }}>
          <div style={{ width:46,height:46,borderRadius:14,background:'linear-gradient(135deg,#7c3aed,#a855f7)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0,boxShadow:'0 4px 16px rgba(124,58,237,.45)' }}>↔</div>
          <div>
            <div style={{ color:'#fff',fontWeight:800,fontSize:16 }}>Reassign Mentor</div>
            <div style={{ color:'#a78bfa',fontSize:12,marginTop:3 }}>{studentName} · {studentId}</div>
          </div>
          <button onClick={onClose} style={{ marginLeft:'auto',background:'none',border:'none',color:'rgba(255,255,255,.4)',fontSize:22,cursor:'pointer',lineHeight:1,padding:0 }}>×</button>
        </div>

        <div style={{ marginBottom:16 }}>
          <div style={{ color:'rgba(255,255,255,.4)',fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',marginBottom:8 }}>Current Mentor</div>
          <div style={{ display:'inline-flex',alignItems:'center',gap:8,padding:'8px 14px',borderRadius:10,background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.1)' }}>
            <div style={{ width:28,height:28,borderRadius:8,background:'rgba(240,180,41,.15)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:900,color:'#F0B429' }}>
              {currentMentorName.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
            </div>
            <span style={{ color:'rgba(255,255,255,.7)',fontSize:13,fontWeight:600 }}>{currentMentorName}</span>
          </div>
        </div>

        <div style={{ textAlign:'center',color:'#a78bfa',fontSize:20,marginBottom:14 }}>↓</div>

        <div style={{ marginBottom:20 }}>
          <div style={{ color:'rgba(255,255,255,.4)',fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',marginBottom:8 }}>New Mentor</div>
          {loadingM ? <div style={{ color:'rgba(255,255,255,.3)',fontSize:13 }}>Loading…</div> : (
            <div style={{ display:'flex',flexDirection:'column',gap:6,maxHeight:240,overflowY:'auto',paddingRight:4 }}>
              {mentors.map(m => (
                <button key={m.mentorId} onClick={() => setSelectedId(m.mentorId)} style={{
                  display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:12,
                  border:`2px solid ${selectedId===m.mentorId?'#a855f7':'rgba(255,255,255,.08)'}`,
                  background:selectedId===m.mentorId?'rgba(168,85,247,.15)':'rgba(255,255,255,.04)',
                  cursor:'pointer',textAlign:'left',transition:'all .15s',
                }}>
                  <div style={{ width:32,height:32,borderRadius:9,flexShrink:0,background:selectedId===m.mentorId?'linear-gradient(135deg,#7c3aed,#a855f7)':'rgba(255,255,255,.08)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:900,color:'#fff' }}>
                    {m.mentorName.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ color:'#fff',fontWeight:700,fontSize:13 }}>{m.mentorName}</div>
                    <div style={{ color:'rgba(255,255,255,.35)',fontSize:10 }}>{m.mentorId} · {m.role||'Mentor'}</div>
                  </div>
                  {selectedId===m.mentorId && <div style={{ marginLeft:'auto',color:'#a855f7',fontSize:18 }}>✓</div>}
                </button>
              ))}
              {mentors.length===0 && <div style={{ color:'rgba(255,255,255,.3)',fontSize:13 }}>No other active mentors</div>}
            </div>
          )}
        </div>

        {selectedId && (
          <div style={{ marginBottom:20,padding:'10px 14px',borderRadius:10,background:'rgba(251,191,36,.08)',border:'1px solid rgba(251,191,36,.25)' }}>
            <span style={{ color:'#fbbf24',fontSize:11,fontWeight:600 }}>⚠ Timetable slots cleared — new mentor re-assigns.</span>
          </div>
        )}

        <div style={{ display:'flex',gap:10 }}>
          <button onClick={onClose} style={{ flex:1,padding:'11px 0',borderRadius:12,border:'1.5px solid rgba(255,255,255,.12)',background:'none',color:'rgba(255,255,255,.5)',fontSize:13,fontWeight:700,cursor:'pointer' }}>Cancel</button>
          <button onClick={confirm} disabled={!selectedId||saving} style={{
            flex:2,padding:'11px 0',borderRadius:12,border:'none',
            background:selectedId?'linear-gradient(135deg,#7c3aed,#a855f7)':'rgba(255,255,255,.08)',
            color:selectedId?'#fff':'rgba(255,255,255,.3)',
            fontSize:13,fontWeight:800,cursor:selectedId?'pointer':'not-allowed',
            boxShadow:selectedId?'0 4px 20px rgba(124,58,237,.4)':'none',
            transition:'all .2s',display:'flex',alignItems:'center',justifyContent:'center',gap:6,
          }}>
            {saving ? <><div style={{ width:14,height:14,border:'2px solid rgba(255,255,255,.3)',borderTopColor:'#fff',borderRadius:'50%',animation:'scSpin 1s linear infinite' }}/> Reassigning…</> : <>↔ Confirm {chosen?`→ ${chosen.mentorName}`:''}</>}
          </button>
        </div>
      </div>
    </>
  );
};

// ─── DEACTIVATE STUDENT MODAL ─────────────────────────────────────────────────
// The same choice as the Students page: Course Finished or Ran Away. A Student
// Coordinator finishing a course raises Class Over instead, so admin is
// emailed the class report. Sits above the student popup.

type DeactivateReason = 'courseFinished' | 'ranAway';

export const DeactivateStudentModal: React.FC<{
  studentId: string; studentName: string;
  adminPassword: string;
  onClose: () => void;
  onDone: () => void;
}> = ({ studentId, studentName, adminPassword, onClose, onDone }) => {
  const [saving, setSaving] = useState<DeactivateReason | null>(null);
  const [error, setError]   = useState('');
  const coordinatorId = adminPassword.startsWith('COORD:') ? adminPassword.slice(6) : '';

  const choose = async (reason: DeactivateReason) => {
    if (saving) return;
    setSaving(reason);
    setError('');
    try {
      if (reason === 'courseFinished' && coordinatorId) await raiseClassOver(coordinatorId, studentId, '');
      else await removeStudent(adminPassword, studentId, reason);
      toast.success(
        reason === 'ranAway' ? `${studentName} marked as Ran Away and deactivated`
        : coordinatorId ? `Class Over raised for ${studentName} — admin has been notified`
        : `${studentName} deactivated (Course Finished)`
      );
      onDone();
    } catch (err: any) {
      setError(err.message);
      setSaving(null);
    }
  };

  const OPTIONS: Array<{ reason: DeactivateReason; icon: string; title: string; sub: string; color: string; rgb: string }> = [
    { reason: 'courseFinished', icon: '✅', title: 'Course Finished',
      sub: coordinatorId ? 'Raises Class Over — admin is emailed the class report' : 'Completed the course',
      color: '#4ade80', rgb: '74,222,128' },
    { reason: 'ranAway', icon: '🚩', title: 'Ran Away', sub: 'Left without finishing the course',
      color: '#f472b6', rgb: '244,114,182' },
  ];

  return (
    <>
      <Overlay onClick={saving ? undefined : onClose} zIndex={10300} />
      <div className="sc-modal-box" style={{
        position:'fixed',top:'50%',left:'50%',zIndex:10301,
        width:440,maxWidth:'calc(100vw - 32px)',
        background:'linear-gradient(145deg,#1a1330,#2b2240)',
        borderRadius:22,padding:28,
        boxShadow:'0 32px 80px rgba(0,0,0,.7),0 0 0 1px rgba(248,113,113,.25)',
      }}>
        <div style={{ display:'flex',alignItems:'flex-start',gap:14,marginBottom:18 }}>
          <div style={{ width:46,height:46,borderRadius:14,background:'linear-gradient(135deg,#dc2626,#f87171)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:20,color:'#fff',flexShrink:0,boxShadow:'0 4px 16px rgba(220,38,38,.4)' }}>⊘</div>
          <div style={{ minWidth:0 }}>
            <div style={{ color:'#fff',fontWeight:800,fontSize:16 }}>Deactivate Student</div>
            <div style={{ color:'#a78bfa',fontSize:12,marginTop:3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{studentName} · {studentId}</div>
          </div>
          <button onClick={onClose} disabled={!!saving} style={{ marginLeft:'auto',background:'none',border:'none',color:'rgba(255,255,255,.4)',fontSize:22,cursor:'pointer',lineHeight:1,padding:0 }}>×</button>
        </div>

        <div style={{ color:'rgba(255,255,255,.6)',fontSize:12,lineHeight:1.6,marginBottom:16 }}>
          Why is this student leaving? They're taken off the timetable and moved to inactive students — Reactivate on the Students page brings them back.
        </div>

        <div style={{ display:'flex',flexDirection:'column',gap:8,marginBottom:16 }}>
          {OPTIONS.map(o => (
            <button key={o.reason} onClick={() => choose(o.reason)} disabled={!!saving} className="sc-btn-hover" style={{
              display:'flex',alignItems:'center',gap:12,padding:'12px 14px',borderRadius:12,textAlign:'left',
              border:`1.5px solid rgba(${o.rgb},.45)`,background:`rgba(${o.rgb},.08)`,
              cursor:saving ? 'default' : 'pointer',opacity:saving && saving !== o.reason ? .45 : 1,transition:'opacity .15s',
            }}>
              <span style={{ fontSize:18,flexShrink:0 }}>{o.icon}</span>
              <span style={{ flex:1,minWidth:0 }}>
                <span style={{ display:'block',color:o.color,fontWeight:800,fontSize:13 }}>{o.title}</span>
                <span style={{ display:'block',color:'rgba(255,255,255,.45)',fontSize:11,marginTop:2 }}>{o.sub}</span>
              </span>
              {saving === o.reason && <div style={{ width:14,height:14,border:'2px solid rgba(255,255,255,.3)',borderTopColor:'#fff',borderRadius:'50%',animation:'scSpin 1s linear infinite',flexShrink:0 }}/>}
            </button>
          ))}
        </div>

        {error && (
          <div style={{ marginBottom:14,padding:'10px 14px',borderRadius:10,background:'rgba(248,113,113,.1)',border:'1px solid rgba(248,113,113,.3)',color:'#fca5a5',fontSize:12 }}>
            {error}
          </div>
        )}

        <button onClick={onClose} disabled={!!saving} style={{ width:'100%',padding:'11px 0',borderRadius:12,border:'1.5px solid rgba(255,255,255,.12)',background:'none',color:'rgba(255,255,255,.5)',fontSize:13,fontWeight:700,cursor:saving ? 'default' : 'pointer' }}>Cancel</button>
      </div>
    </>
  );
};

// ─── STUDENT DETAIL MODAL (centered) ─────────────────────────────────────────

export const StudentDetailModal: React.FC<{
  studentId: string;
  adminPassword: string;
  onClose: () => void;
  onReassign: (t: { studentId:string; studentName:string; currentMentorId:string; currentMentorName:string }) => void;
  onDeactivated?: (studentId: string) => void;   // so the page behind can drop / reload the student
}> = ({ studentId, adminPassword, onClose, onReassign, onDeactivated }) => {
  const [detail, setDetail] = useState<StudentFullDetail | null>(null);
  const [portions, setPortions] = useState<PortionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'overview'|'classes'|'progress'|'login'>('overview');
  const [deactivating, setDeactivating] = useState(false);
  const deactivatingRef = useRef(false);
  deactivatingRef.current = deactivating;

  useEffect(() => {
    let live = true;
    setLoading(true); setDetail(null); setPortions(null); setTab('overview');
    Promise.all([
      getStudentFullDetail(adminPassword, studentId),
      // The curriculum with its day titles; without it the Progress tab falls
      // back to plain day numbers
      getStudentPortions(adminPassword, studentId).catch(() => null),
    ])
      .then(([d, p]) => { if (live) { setDetail(d as any); setPortions(p); } })
      .catch(err => { if (live) { toast.error(err.message); onClose(); } })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [studentId, adminPassword, onClose]);

  useEffect(() => {
    // Escape closes the Deactivate dialog first, if it's open
    const h = (e:KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (deactivatingRef.current) setDeactivating(false);
      else onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const R=34, CIRC=2*Math.PI*R;
  const attPct = detail?.stats.attPct ?? 0;
  const attOff = CIRC*(1-attPct/100);

  return (
    <>
      <Overlay onClick={onClose} zIndex={10200} />
      <div className="sc-modal-box" style={{
        position:'fixed', top:'50%', left:'50%', zIndex:10201,
        width:780, maxWidth:'calc(100vw - 24px)',
        maxHeight:'88vh',
        background:'linear-gradient(165deg,#1a1330 0%,#1e1535 55%,#241840 100%)',
        borderRadius:24,
        boxShadow:'0 40px 100px rgba(0,0,0,.75),0 0 0 1px rgba(240,180,41,.15)',
        display:'flex', flexDirection:'column', overflow:'hidden',
      }}>
        {/* ── Header ── */}
        <div style={{ padding:'22px 28px 0', background:'linear-gradient(135deg,rgba(43,34,64,.9),rgba(30,23,53,.9))', borderBottom:'1px solid rgba(255,255,255,.07)', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:14, marginBottom:18, flexWrap:'wrap' }}>
            {detail ? (
              <>
                <div style={{
                  width:54,height:54,borderRadius:16,flexShrink:0,
                  background:'linear-gradient(135deg,#7c3aed,#a855f7)',
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:20,fontWeight:900,color:'#fff',
                  boxShadow:'0 6px 20px rgba(124,58,237,.5)',
                }}>
                  {detail.student.name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                </div>
                <div style={{ flex:'1 1 200px',minWidth:0 }}>
                  <div style={{ display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:4 }}>
                    <span style={{ color:'#fff',fontWeight:900,fontSize:20 }}>{detail.student.name}</span>
                    <span style={{
                      fontSize:10,fontWeight:800,padding:'2px 8px',borderRadius:6,
                      background:`${STATUS_COLORS[detail.student.status]??'#94a3b8'}22`,
                      color:STATUS_COLORS[detail.student.status]??'#94a3b8',
                      border:`1px solid ${STATUS_COLORS[detail.student.status]??'#94a3b8'}44`,
                    }}>{detail.student.status}</span>
                  </div>
                  <div style={{ color:'#a78bfa',fontSize:12,marginBottom:2 }}>{detail.student.studentId} · {detail.student.course}</div>
                  <div style={{ color:'rgba(255,255,255,.4)',fontSize:11 }}>
                    Mentor: <span style={{ color:'rgba(255,255,255,.7)' }}>{detail.student.mentorName}</span>
                    {detail.student.phase && <span style={{ color:'rgba(255,255,255,.3)',marginLeft:8 }}>· {detail.student.phase}</span>}
                  </div>
                </div>
              </>
            ) : (
              <div style={{ display:'flex',alignItems:'center',gap:12 }}>
                <div style={{ width:54,height:54,borderRadius:16,background:'rgba(255,255,255,.06)' }}/>
                <div style={{ height:20,width:180,borderRadius:6,background:'rgba(255,255,255,.08)' }}/>
              </div>
            )}
            <div style={{ display:'flex',gap:8,flexShrink:0,marginLeft:'auto' }}>
              {detail && (
                <button onClick={() => onReassign({ studentId: detail.student.studentId, studentName: detail.student.name, currentMentorId: detail.student.mentorId, currentMentorName: detail.student.mentorName })}
                  style={{ padding:'7px 14px',borderRadius:10,border:'1.5px solid rgba(167,139,250,.4)',background:'rgba(167,139,250,.1)',color:'#c4b5fd',fontSize:11,fontWeight:700,cursor:'pointer' }}>
                  ↔ Reassign
                </button>
              )}
              {detail && detail.student.status === 'Active' && (
                <button onClick={() => setDeactivating(true)} className="sc-btn-hover"
                  style={{ padding:'7px 14px',borderRadius:10,border:'1.5px solid rgba(248,113,113,.4)',background:'rgba(248,113,113,.1)',color:'#fca5a5',fontSize:11,fontWeight:700,cursor:'pointer' }}>
                  ⊘ Deactivate
                </button>
              )}
              <button onClick={onClose} style={{ width:34,height:34,borderRadius:9,border:'1px solid rgba(255,255,255,.12)',background:'rgba(255,255,255,.06)',color:'rgba(255,255,255,.5)',fontSize:20,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center' }}>×</button>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display:'flex' }}>
            {(['overview','classes','progress','login'] as const).map(t => (
              <button key={t} className="sc-tab" onClick={() => setTab(t)} style={{
                padding:'10px 18px', fontSize:12, fontWeight:700,
                color:tab===t?'#F0B429':'rgba(255,255,255,.4)',
                borderBottom:tab===t?'2px solid #F0B429':'2px solid transparent',
                marginBottom:-1,
              }}>{t==='login'?'Login Info':t.charAt(0).toUpperCase()+t.slice(1)}</button>
            ))}
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ flex:1, overflowY:'auto', padding:'24px 28px' }}>
          {loading ? (
            <div style={{ display:'flex',flexDirection:'column',gap:12 }}>
              {[100,65,85,50].map((w,i) => (
                <div key={i} style={{ height:14,borderRadius:6,background:'rgba(255,255,255,.07)',width:`${w}%` }}/>
              ))}
            </div>
          ) : detail ? (
            <>
              {/* ── OVERVIEW ── */}
              {tab==='overview' && (
                <div style={{ display:'flex',flexDirection:'column',gap:20 }}>
                  {/* Top row: ring + 3 pills + info grid side-by-side (stacked on a phone) */}
                  <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))',gap:16 }}>
                    {/* Left: attendance + task */}
                    <div style={{ display:'flex',flexDirection:'column',gap:16 }}>
                      <div style={{ padding:16,borderRadius:14,background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.07)' }}>
                        <div style={{ color:'rgba(255,255,255,.4)',fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',marginBottom:14 }}>Attendance</div>
                        <div style={{ display:'flex',alignItems:'center',gap:14 }}>
                          <svg width={80} height={80}>
                            <circle cx={40} cy={40} r={R} fill="none" stroke="rgba(255,255,255,.08)" strokeWidth={7}/>
                            <circle cx={40} cy={40} r={R} fill="none" stroke={ATT_COLOR(attPct)} strokeWidth={7} strokeLinecap="round"
                              strokeDasharray={CIRC} strokeDashoffset={attOff}
                              style={{ transform:'rotate(-90deg)',transformOrigin:'40px 40px',transition:'stroke-dashoffset .8s ease' }}/>
                            <text x={40} y={40} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={14} fontWeight={900}>{attPct}%</text>
                          </svg>
                          <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
                            <Pill label="Total" value={detail.stats.totalLogs}/>
                            <Pill label="Present" value={detail.stats.attended} color="#4ade80"/>
                            <Pill label="Absent" value={detail.stats.absent} color="#f87171"/>
                          </div>
                        </div>
                      </div>

                      {detail.stats.taskTotal>0 && (
                        <div style={{ padding:16,borderRadius:14,background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.07)' }}>
                          <div style={{ color:'rgba(255,255,255,.4)',fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',marginBottom:12 }}>Tasks</div>
                          <div style={{ display:'flex',gap:8,flexWrap:'wrap',marginBottom:12 }}>
                            <Pill label="Given" value={detail.stats.taskTotal}/>
                            <Pill label="Done" value={detail.stats.taskDone} color="#4ade80"/>
                            <Pill label="Missed" value={detail.stats.taskMiss} color="#f87171"/>
                            <Pill label="Pending" value={detail.stats.taskPending} color="#fbbf24"/>
                          </div>
                          <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:6 }}>
                            <span style={{ color:'#4ade80',fontSize:10,fontWeight:700,width:52 }}>Done</span>
                            <ProgBar pct={detail.stats.taskTotal?Math.round(detail.stats.taskDone/detail.stats.taskTotal*100):0} color="linear-gradient(90deg,#22c55e,#4ade80)"/>
                            <span style={{ color:'#4ade80',fontSize:10,fontWeight:700,width:32,textAlign:'right' }}>{detail.stats.taskTotal?Math.round(detail.stats.taskDone/detail.stats.taskTotal*100):0}%</span>
                          </div>
                          <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                            <span style={{ color:'#f87171',fontSize:10,fontWeight:700,width:52 }}>Missed</span>
                            <ProgBar pct={detail.stats.taskTotal?Math.round(detail.stats.taskMiss/detail.stats.taskTotal*100):0} color="linear-gradient(90deg,#ef4444,#f87171)"/>
                            <span style={{ color:'#f87171',fontSize:10,fontWeight:700,width:32,textAlign:'right' }}>{detail.stats.taskTotal?Math.round(detail.stats.taskMiss/detail.stats.taskTotal*100):0}%</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right: profile + payment */}
                    <div style={{ display:'flex',flexDirection:'column',gap:16 }}>
                      <div style={{ padding:16,borderRadius:14,background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.07)' }}>
                        <div style={{ color:'rgba(255,255,255,.4)',fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',marginBottom:12 }}>Profile</div>
                        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px 16px' }}>
                          {[
                            ['Email', detail.student.email||'—'],
                            ['Phone', detail.student.phone||'—'],
                            ['Enrolled', detail.student.dateJoined||'—'],
                            ['Session', detail.payment?.sessionType||'—'],
                            ['Sales', detail.student.salesPerson||'—'],
                            ['Mentor', detail.student.mentorName],
                          ].map(([lbl,val]) => (
                            <div key={lbl}>
                              <div style={{ color:'rgba(255,255,255,.3)',fontSize:10,fontWeight:600,marginBottom:2 }}>{lbl}</div>
                              <div style={{ color:'rgba(255,255,255,.82)',fontSize:12,fontWeight:600,wordBreak:'break-word' }}>{val}</div>
                            </div>
                          ))}
                        </div>
                        {detail.student.notes && (
                          <div style={{ marginTop:12,padding:'8px 12px',borderRadius:8,background:'rgba(240,180,41,.06)',border:'1px solid rgba(240,180,41,.15)' }}>
                            <div style={{ color:'rgba(240,180,41,.6)',fontSize:9,fontWeight:700,letterSpacing:1,textTransform:'uppercase',marginBottom:4 }}>Notes</div>
                            <div style={{ color:'rgba(255,255,255,.65)',fontSize:11 }}>{detail.student.notes}</div>
                          </div>
                        )}
                      </div>

                      {detail.payment && (
                        <div style={{ padding:16,borderRadius:14,background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.07)' }}>
                          <div style={{ color:'rgba(255,255,255,.4)',fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',marginBottom:12 }}>Payment</div>
                          <div style={{ display:'flex',gap:8,flexWrap:'wrap',marginBottom:12 }}>
                            <Pill label="Paid" value={`₹${(detail.payment.totalPaid||0).toLocaleString()}`} color="#4ade80"/>
                            <Pill label="Fee" value={`₹${(detail.payment.courseFee||0).toLocaleString()}`}/>
                            <Pill label="Balance" value={`₹${Math.max(0,(detail.payment.courseFee||0)-(detail.payment.totalPaid||0)).toLocaleString()}`} color="#fbbf24"/>
                          </div>
                          <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                            <ProgBar pct={detail.payment.courseFee?Math.round(detail.payment.totalPaid/detail.payment.courseFee*100):0} color="linear-gradient(90deg,#22c55e,#4ade80)"/>
                            <span style={{ fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:6,background:detail.payment.status==='Fully Paid'?'rgba(74,222,128,.15)':'rgba(251,191,36,.1)',color:detail.payment.status==='Fully Paid'?'#4ade80':'#fbbf24' }}>{detail.payment.status||'Partial'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ── CLASSES ── */}
              {tab==='classes' && (
                <div>
                  <div style={{ color:'rgba(255,255,255,.4)',fontSize:11,marginBottom:14 }}>Last {detail.recentLogs.length} classes (newest first)</div>
                  <div style={{ display:'flex',flexDirection:'column',gap:5 }}>
                    {detail.recentLogs.map((log,i) => (
                      <div key={i} style={{
                        display:'grid',gridTemplateColumns:'88px 1fr 72px 100px',
                        gap:10,alignItems:'center',padding:'10px 14px',borderRadius:10,
                        background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.06)',
                      }}>
                        <div>
                          <div style={{ color:'rgba(255,255,255,.7)',fontSize:11,fontWeight:600 }}>{log.date}</div>
                          <div style={{ color:'rgba(255,255,255,.3)',fontSize:10 }}>{log.day}</div>
                        </div>
                        <div>
                          <div style={{ color:'#fff',fontSize:12,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{log.topic||'—'}</div>
                          <div style={{ color:'rgba(255,255,255,.3)',fontSize:10,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{log.takenBy}</div>
                        </div>
                        <span style={{ fontSize:9,fontWeight:800,padding:'3px 7px',borderRadius:5,textAlign:'center',background:log.attendance==='Present'?'rgba(74,222,128,.15)':'rgba(248,113,113,.15)',color:log.attendance==='Present'?'#4ade80':'#f87171' }}>{log.attendance}</span>
                        <span style={{ fontSize:9,fontWeight:700,padding:'3px 7px',borderRadius:5,textAlign:'center',...TASK_COLOR(log.completionStatus) }}>{log.completionStatus||'N/A'}</span>
                      </div>
                    ))}
                    {detail.recentLogs.length===0 && <div style={{ color:'rgba(255,255,255,.3)',textAlign:'center',padding:'40px 0' }}>No class records</div>}
                  </div>
                </div>
              )}

              {/* ── PROGRESS ── */}
              {tab==='progress' && (
                <div style={{ display:'flex',flexDirection:'column',gap:20 }}>
                  {portions && (
                    <CurriculumRoadmap
                      tone="dark"
                      title={portions.curriculumTitle}
                      days={portions.days}
                      completedDays={portions.completedDays}
                      checkpoints={portions.projectCheckpoints}
                      projectStatuses={portions.projectStatuses}
                    />
                  )}
                  {detail.progress ? (
                    <>
                      {!portions && <div style={{ padding:16,borderRadius:14,background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.07)' }}>
                        <div style={{ color:'rgba(255,255,255,.4)',fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',marginBottom:12 }}>Curriculum: {detail.progress.curriculumKey||'Not Set'}</div>
                        <div style={{ display:'flex',alignItems:'baseline',gap:8,marginBottom:14 }}>
                          <span style={{ color:'#4ade80',fontWeight:900,fontSize:28 }}>{detail.progress.completedDays.length}</span>
                          <span style={{ color:'rgba(255,255,255,.4)',fontSize:13 }}>days completed</span>
                        </div>
                        {detail.progress.completedDays.length>0 && (
                          <div style={{ display:'flex',flexWrap:'wrap',gap:5 }}>
                            {detail.progress.completedDays.map(d => (
                              <span key={d} style={{ width:32,height:32,borderRadius:8,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(74,222,128,.18)',color:'#4ade80',fontSize:11,fontWeight:800 }}>D{d}</span>
                            ))}
                          </div>
                        )}
                      </div>}
                      {detail.progress.taskLogs.length>0 && (
                        <div style={{ padding:16,borderRadius:14,background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.07)' }}>
                          <div style={{ color:'rgba(255,255,255,.4)',fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',marginBottom:12 }}>Recent Task Logs</div>
                          <div style={{ display:'flex',flexDirection:'column',gap:8 }}>
                            {detail.progress.taskLogs.map((tl,i) => (
                              <div key={i} style={{ display:'flex',alignItems:'center',gap:12 }}>
                                <span style={{ color:'rgba(255,255,255,.4)',fontSize:11,width:86,flexShrink:0 }}>{tl.date}</span>
                                <ProgBar pct={tl.tasksGiven?Math.round(tl.tasksCompleted/tl.tasksGiven*100):0} color="linear-gradient(90deg,#6366f1,#818cf8)"/>
                                <span style={{ color:'#818cf8',fontSize:11,fontWeight:700,width:52,textAlign:'right',flexShrink:0 }}>{tl.tasksCompleted}/{tl.tasksGiven}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : !portions && <div style={{ color:'rgba(255,255,255,.3)',textAlign:'center',padding:'40px 0' }}>No progress data</div>}
                </div>
              )}

              {/* ── LOGIN ── */}
              {tab==='login' && (
                <div style={{ display:'flex',flexDirection:'column',gap:16 }}>
                  {detail.auth ? (
                    <>
                      <div style={{ padding:20,borderRadius:16,background:'rgba(255,255,255,.04)',border:'1px solid rgba(255,255,255,.08)' }}>
                        <div style={{ color:'rgba(255,255,255,.4)',fontSize:10,fontWeight:700,letterSpacing:1,textTransform:'uppercase',marginBottom:16 }}>Portal Login</div>
                        {[
                          { label:'Student ID (username)', value:detail.student.studentId, color:'#F0B429' },
                          { label:'Login Email', value:detail.auth.email, color:'#a78bfa' },
                        ].map(({ label, value, color }) => (
                          <div key={label} style={{ marginBottom:14 }}>
                            <div style={{ color:'rgba(255,255,255,.35)',fontSize:10,fontWeight:600,marginBottom:4 }}>{label}</div>
                            <div style={{ display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:10,background:'rgba(255,255,255,.06)',border:'1px solid rgba(255,255,255,.1)' }}>
                              <span style={{ fontFamily:'monospace',color,fontWeight:800,fontSize:14,flex:1,wordBreak:'break-all' }}>{value}</span>
                              <button onClick={() => { navigator.clipboard.writeText(value); toast.success('Copied!'); }} style={{ background:'none',border:'none',color:'rgba(255,255,255,.35)',fontSize:16,cursor:'pointer' }} title="Copy">⎘</button>
                            </div>
                          </div>
                        ))}
                        <div>
                          <div style={{ color:'rgba(255,255,255,.35)',fontSize:10,fontWeight:600,marginBottom:8 }}>Account Status</div>
                          <span style={{ padding:'6px 14px',borderRadius:8,fontSize:12,fontWeight:700,background:detail.auth.isFirstLogin?'rgba(251,191,36,.12)':'rgba(74,222,128,.12)',color:detail.auth.isFirstLogin?'#fbbf24':'#4ade80',border:`1px solid ${detail.auth.isFirstLogin?'rgba(251,191,36,.3)':'rgba(74,222,128,.3)'}` }}>
                            {detail.auth.isFirstLogin?'⚠ Never Logged In':'✓ Account Active'}
                          </span>
                        </div>
                      </div>
                      <div style={{ padding:'14px 18px',borderRadius:12,background:'rgba(240,180,41,.06)',border:'1px solid rgba(240,180,41,.15)' }}>
                        <div style={{ color:'#fbbf24',fontSize:11,fontWeight:700,marginBottom:4 }}>Portal Login Instructions</div>
                        <div style={{ color:'rgba(255,255,255,.55)',fontSize:11,lineHeight:1.6 }}>
                          Student logs in with <strong style={{ color:'#F0B429' }}>Student ID</strong> as username.
                          {detail.auth.isFirstLogin&&' They have not logged in yet — share their Student ID to get started.'}
                        </div>
                      </div>
                    </>
                  ) : (
                    <div style={{ padding:20,borderRadius:16,background:'rgba(248,113,113,.06)',border:'1px solid rgba(248,113,113,.2)',color:'#fca5a5',textAlign:'center' }}>
                      No portal account registered yet.
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>

      {deactivating && detail && (
        <DeactivateStudentModal
          studentId={detail.student.studentId}
          studentName={detail.student.name}
          adminPassword={adminPassword}
          onClose={() => setDeactivating(false)}
          onDone={() => { setDeactivating(false); onDeactivated?.(detail.student.studentId); onClose(); }}
        />
      )}
    </>
  );
};

// ─── GLOBAL OVERLAY (rendered by TrackerLayout) ───────────────────────────────

export const StudentCardOverlay: React.FC = () => {
  const {
    hovered, onCardEnter, onCardLeave, canSeeDetail,
    openDetail, openReassign,
    detailStudentId, closeDetail, adminPassword,
    reassignTarget, closeReassign, notifyStudentChange,
  } = useStudentCard();

  return (
    <>
      <style>{STYLES}</style>

      {hovered && (
        <StudentHoverCard
          studentId={hovered.studentId}
          studentName={hovered.studentName}
          pos={hovered.pos}
          onCardEnter={onCardEnter}
          onCardLeave={onCardLeave}
          onViewDetails={canSeeDetail ? () => openDetail(hovered.studentId) : undefined}
        />
      )}

      {detailStudentId && canSeeDetail && (
        <StudentDetailModal
          studentId={detailStudentId}
          adminPassword={adminPassword}
          onClose={closeDetail}
          onReassign={(t) => { closeDetail(); openReassign(t); }}
          onDeactivated={notifyStudentChange}
        />
      )}

      {reassignTarget && canSeeDetail && (
        <ReassignMentorModal
          studentId={reassignTarget.studentId}
          studentName={reassignTarget.studentName}
          currentMentorId={reassignTarget.currentMentorId}
          currentMentorName={reassignTarget.currentMentorName}
          adminPassword={adminPassword}
          onClose={closeReassign}
          onDone={() => { notifyStudentChange(reassignTarget.studentId); closeReassign(); }}
        />
      )}
    </>
  );
};
